import { useEffect, useRef, useState, useCallback } from "react";
import { createChart, CandlestickSeries, LineStyle, CrosshairMode } from "lightweight-charts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RefreshCw } from "lucide-react";
import api from "@/lib/api";

const TF_OPTIONS = [1, 5, 15];

function aggregate(candles, tfMin) {
  if (tfMin <= 1) return candles;
  const bucketSec = tfMin * 60;
  const out = [];
  let cur = null;
  for (const c of candles) {
    const bucket = Math.floor(c.time / bucketSec) * bucketSec;
    if (!cur || cur.time !== bucket) {
      if (cur) out.push(cur);
      cur = { time: bucket, open: c.open, high: c.high, low: c.low, close: c.close };
    } else {
      cur.high = Math.max(cur.high, c.high);
      cur.low = Math.min(cur.low, c.low);
      cur.close = c.close;
    }
  }
  if (cur) out.push(cur);
  return out;
}

/**
 * TradingView-style live chart popup.
 * config = { symbol, exchange, expiry, refPrice, subtitle,
 *            levels: [{ price, title, color, dashed }] }
 */
export const TradeChartModal = ({ open, onClose, config }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const [tf, setTf] = useState(1);
  const [candles, setCandles] = useState([]);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchCandles = useCallback(async (silent = false) => {
    if (!config?.symbol) return;
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({ symbol: config.symbol, exchange: config.exchange || "NSE" });
      if (config.expiry) params.set("expiry", config.expiry);
      if (config.refPrice) params.set("ref_price", String(config.refPrice));
      const res = await api.get(`/charts/candles?${params.toString()}`);
      setCandles(res.data.data.candles || []);
      setMeta(res.data.data);
      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load chart data");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [config]);

  // Fetch on open + live refresh every 10s
  useEffect(() => {
    if (!open) return;
    setCandles([]);
    setError(null);
    setTf(1);
    fetchCandles();
    const id = setInterval(() => fetchCandles(true), 10000);
    return () => clearInterval(id);
  }, [open, fetchCandles]);

  // Create/destroy chart with the dialog
  useEffect(() => {
    if (!open) return;
    let chart;
    const t = setTimeout(() => {
      if (!containerRef.current) return;
      chart = createChart(containerRef.current, {
        autoSize: true,
        localization: { locale: "en-IN" },
        layout: {
          background: { color: "#0c0d10" },
          textColor: "#94a3b8",
          fontSize: 11,
        },
        grid: {
          vertLines: { color: "rgba(255,255,255,0.04)" },
          horzLines: { color: "rgba(255,255,255,0.04)" },
        },
        crosshair: { mode: CrosshairMode.Normal },
        timeScale: { timeVisible: true, secondsVisible: false, borderColor: "rgba(255,255,255,0.1)" },
        rightPriceScale: { borderColor: "rgba(255,255,255,0.1)" },
      });
      const series = chart.addSeries(CandlestickSeries, {
        upColor: "#10b981", downColor: "#f43f5e",
        borderUpColor: "#10b981", borderDownColor: "#f43f5e",
        wickUpColor: "#10b981", wickDownColor: "#f43f5e",
      });
      (config?.levels || []).forEach((l) => {
        if (l.price === null || l.price === undefined || !(l.price > 0)) return;
        series.createPriceLine({
          price: Number(l.price),
          color: l.color,
          lineWidth: 2,
          lineStyle: l.dashed ? LineStyle.Dashed : LineStyle.Solid,
          axisLabelVisible: true,
          title: l.title,
        });
      });
      chartRef.current = chart;
      seriesRef.current = series;
    }, 60); // let the dialog mount/animate first
    return () => {
      clearTimeout(t);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        seriesRef.current = null;
      } else if (chart) {
        chart.remove();
      }
    };
  }, [open, config]);

  // Push data on candles / timeframe change
  useEffect(() => {
    if (!seriesRef.current || !candles.length) return;
    seriesRef.current.setData(aggregate(candles, tf));
    chartRef.current?.timeScale().fitContent();
  }, [candles, tf]);

  const cmp = candles.length ? candles[candles.length - 1].close : null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl bg-[#0c0d10] border-white/10 p-0 gap-0" data-testid="trade-chart-modal">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-white/10">
          <div className="flex flex-wrap items-center justify-between gap-3 pr-8">
            <div>
              <DialogTitle className="text-white text-base flex items-center gap-2.5" data-testid="chart-modal-title">
                {config?.symbol}
                <span className="text-[10px] font-mono text-slate-500">{meta?.symbol || config?.exchange}</span>
                {meta?.source === "DEMO" && (
                  <span className="text-[9px] font-mono text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded px-1.5 py-0.5">DEMO DATA</span>
                )}
                {cmp !== null && (
                  <span className="text-sm font-mono text-cyan-400" data-testid="chart-cmp">
                    ₹{cmp.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </span>
                )}
              </DialogTitle>
              {config?.subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{config.subtitle}</p>}
            </div>
            <div className="flex items-center gap-3">
              {/* Level chips */}
              <div className="hidden md:flex items-center gap-2">
                {(config?.levels || []).filter((l) => l.price > 0).map((l, i) => (
                  <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 rounded border"
                    style={{ color: l.color, borderColor: `${l.color}55`, backgroundColor: `${l.color}14` }}>
                    {l.title} ₹{Number(l.price).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </span>
                ))}
              </div>
              {/* Timeframe toggle */}
              <div className="flex items-center gap-0.5 p-0.5 bg-black/40 rounded border border-white/10">
                {TF_OPTIONS.map((m) => (
                  <button key={m} data-testid={`chart-tf-${m}m`} onClick={() => setTf(m)}
                    className={`px-2 py-1 rounded text-[10px] font-mono transition-all ${
                      tf === m ? "bg-cyan-500/20 text-cyan-400" : "text-slate-500 hover:text-slate-300"
                    }`}>
                    {m}m
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="relative">
          <div ref={containerRef} className="w-full h-[460px]" data-testid="chart-container" />
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <RefreshCw className="w-5 h-5 text-cyan-400 animate-spin" />
            </div>
          )}
          {error && !loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded px-4 py-2" data-testid="chart-error">
                {error}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-2 border-t border-white/10 flex items-center justify-between">
          <span className="text-[10px] text-slate-600 font-mono">
            1-min live candles · auto-refresh 10s{lastUpdate ? ` · updated ${lastUpdate.toLocaleTimeString("en-IN", { hour12: false })}` : ""}
          </span>
          <span className="text-[10px] text-slate-600 font-mono">Powered by lightweight-charts</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};
